// v2
// Path: frontend/src/components/QuickBooksSync/StandardMappingsModal.jsx
// Inputs: token, onClose, onSaved
// Outputs: React JSX modal for managing, editing, and resetting standard field mapping presets across source systems and QuickBooks
// Summary: Interface for viewing, configuring, and updating standard field mappings stored in Sync.StandardMappings without redundant enable checkboxes and supporting all QuickBooks entities.

import React, { useState, useEffect } from 'react';

export const StandardMappingsModal = ({ token, onClose, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [mappings, setMappings] = useState([]);
    const [sourceSystems, setSourceSystems] = useState(['Passport']);
    const [selectedSystem, setSelectedSystem] = useState('Passport');
    const [selectedEntityType, setSelectedEntityType] = useState('Customers');
    const [selectedMapping, setSelectedMapping] = useState(null);
    const [message, setMessage] = useState(null);

    // Form state for current selected mapping
    const [form, setForm] = useState({
        mappingId: null,
        sourceSystem: 'Passport',
        targetSystem: 'QuickBooks',
        entityType: 'Customers',
        targetQBEntity: 'Customer',
        mappingName: '',
        description: '',
        isDefault: true,
        mappings: []
    });

    // Known target QuickBooks entities
    const qbEntities = [
        'Customer', 'Vendor', 'Item', 'Account', 'Invoice', 'InvoiceLine', 'Bill', 'Payment', 'JournalEntry', 'PurchaseOrder'
    ];

    const fetchMappings = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/quickbooks/standard-mappings?sourceSystem=${encodeURIComponent(selectedSystem)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const fetched = Array.isArray(data.mappings) ? data.mappings : [];
                setMappings(fetched);
                if (Array.isArray(data.sourceSystems) && data.sourceSystems.length > 0) {
                    setSourceSystems(data.sourceSystems);
                }

                // Match currently selected entity
                const matched = fetched.find(m => m.EntityType.toLowerCase() === selectedEntityType.toLowerCase()) || fetched[0];
                if (matched) {
                    setSelectedEntityType(matched.EntityType);
                    loadMappingIntoForm(matched);
                } else if (fetched.length === 0) {
                    setSelectedMapping(null);
                    initNewForm(selectedSystem, selectedEntityType);
                }
            }
        } catch (err) {
            console.error('[StandardMappingsModal] Error fetching mappings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMappings();
    }, [selectedSystem]);

    const loadMappingIntoForm = (mappingObj) => {
        setSelectedMapping(mappingObj);
        setForm({
            mappingId: mappingObj.MappingID,
            sourceSystem: mappingObj.SourceSystem,
            targetSystem: mappingObj.TargetSystem || 'QuickBooks',
            entityType: mappingObj.EntityType,
            targetQBEntity: mappingObj.TargetQBEntity,
            mappingName: mappingObj.MappingName || '',
            description: mappingObj.Description || '',
            isDefault: mappingObj.IsDefault !== undefined ? !!mappingObj.IsDefault : true,
            mappings: Array.isArray(mappingObj.mappings) ? JSON.parse(JSON.stringify(mappingObj.mappings)) : []
        });
    };

    const initNewForm = (sys, entType) => {
        setForm({
            mappingId: null,
            sourceSystem: sys,
            targetSystem: 'QuickBooks',
            entityType: entType,
            targetQBEntity: entType === 'Customers' ? 'Customer' : entType === 'Items' ? 'Item' : entType === 'Vendors' ? 'Vendor' : entType === 'Accounts' ? 'Account' : entType === 'Bills' ? 'Bill' : entType === 'Purchase Orders' ? 'PurchaseOrder' : entType === 'Payments' ? 'Payment' : entType === 'Journal Entries' ? 'JournalEntry' : entType === 'Order Lines' ? 'InvoiceLine' : 'Invoice',
            mappingName: `Standard ${sys} ${entType} -> QBO Preset`,
            description: `Default field mappings between ${sys} ${entType} and QuickBooks Online`,
            isDefault: true,
            mappings: [
                { source: '', sources: [], target: '' }
            ]
        });
    };

    const handleSelectEntity = (ent) => {
        setSelectedEntityType(ent);
        const matched = mappings.find(m => m.EntityType.toLowerCase() === ent.toLowerCase());
        if (matched) {
            loadMappingIntoForm(matched);
        } else {
            setSelectedMapping(null);
            initNewForm(selectedSystem, ent);
        }
        setMessage(null);
    };

    const handleAddFieldRow = () => {
        setForm(prev => ({
            ...prev,
            mappings: [
                ...prev.mappings,
                { source: '', sources: [], target: '' }
            ]
        }));
    };

    const handleRemoveFieldRow = (idx) => {
        setForm(prev => ({
            ...prev,
            mappings: prev.mappings.filter((_, i) => i !== idx)
        }));
    };

    const handleFieldChange = (idx, field, val) => {
        setForm(prev => {
            const updated = [...prev.mappings];
            if (field === 'source') {
                updated[idx].source = val;
                updated[idx].sources = val ? [val] : [];
            } else if (field === 'target') {
                updated[idx].target = val;
            }
            return { ...prev, mappings: updated };
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            // Filter out empty rows
            const cleanMappings = form.mappings
                .filter(m => (m.source && m.source.trim()) || (m.target && m.target.trim()))
                .map(m => ({
                    source: (m.source || '').trim(),
                    sources: m.sources && m.sources.length > 0 ? m.sources : [(m.source || '').trim()],
                    target: (m.target || '').trim()
                }));

            const payload = {
                mappingId: form.mappingId,
                sourceSystem: form.sourceSystem,
                targetSystem: form.targetSystem,
                entityType: form.entityType,
                targetQBEntity: form.targetQBEntity,
                mappingName: form.mappingName,
                description: form.description,
                isDefault: form.isDefault,
                mappings: cleanMappings
            };

            const res = await fetch('/api/quickbooks/standard-mappings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: data.message || 'Standard mapping preset saved!' });
                await fetchMappings();
                if (onSaved) onSaved();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to save standard mapping.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleResetDefaults = async () => {
        if (!window.confirm('Reset all standard mappings back to canonical defaults? Any customizations will be refreshed.')) return;
        setSaving(true);
        try {
            const res = await fetch('/api/quickbooks/standard-mappings/reset', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: 'Standard mappings reset to canonical defaults.' });
                await fetchMappings();
                if (onSaved) onSaved();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to reset standard mappings.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    // Standard list of entity categories
    const commonEntities = ['Customers', 'Items', 'Vendors', 'Accounts', 'Orders', 'Order Lines', 'Bills', 'Purchase Orders', 'Payments', 'Journal Entries'];
    const displayedEntities = Array.from(new Set([...commonEntities, ...mappings.map(m => m.EntityType)]));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-850">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                            <span className="material-symbols-outlined text-[24px]">schema</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-white tracking-wide">
                                    Standard Field Mappings Manager
                                </h3>
                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                    Group Reusable
                                </span>
                            </div>
                            <p className="text-xs text-slate-400">
                                Define canonical field mapping presets for source ERP systems that can be applied en masse to integration groups.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleResetDefaults}
                            disabled={saving}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors border border-slate-700"
                            title="Reset standard mappings back to system defaults"
                        >
                            <span className="material-symbols-outlined text-[15px]">restart_alt</span>
                            Reset to Defaults
                        </button>
                        <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                {/* System & Entity Navigation Bar */}
                <div className="px-6 py-3 bg-slate-850/80 border-b border-slate-800 flex flex-wrap items-center justify-between gap-4">
                    {/* Source System Selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                            <span className="material-symbols-outlined text-[15px] text-indigo-400">device_hub</span>
                            Source System:
                        </span>
                        <select
                            value={selectedSystem}
                            onChange={e => setSelectedSystem(e.target.value)}
                            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white font-semibold focus:border-indigo-500 focus:outline-none"
                        >
                            {sourceSystems.map(sys => (
                                <option key={sys} value={sys}>{sys}</option>
                            ))}
                        </select>
                        <span className="text-slate-500 text-xs">&rarr;</span>
                        <span className="px-2 py-1 bg-emerald-950/60 border border-emerald-800 text-emerald-300 rounded text-xs font-semibold flex items-center gap-1">
                            <span className="material-symbols-outlined text-[13px]">cloud</span>
                            QuickBooks Online
                        </span>
                    </div>

                    {/* Entity Tabs */}
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
                        {displayedEntities.map(ent => {
                            const isSelected = selectedEntityType.toLowerCase() === ent.toLowerCase();
                            const hasPreset = mappings.some(m => m.EntityType.toLowerCase() === ent.toLowerCase());
                            return (
                                <button
                                    key={ent}
                                    onClick={() => handleSelectEntity(ent)}
                                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                                        isSelected
                                            ? 'bg-indigo-600 text-white shadow-sm'
                                            : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-750'
                                    }`}
                                >
                                    {hasPreset && (
                                        <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-emerald-300' : 'bg-emerald-400'}`}></span>
                                    )}
                                    {ent}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {message && (
                        <div className={`p-3 rounded-lg text-xs border flex items-center justify-between gap-2 ${message.type === 'success' ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300' : 'bg-red-950/60 border-red-800 text-red-300'}`}>
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-[16px]">
                                    {message.type === 'success' ? 'check_circle' : 'error'}
                                </span>
                                <span>{message.text}</span>
                            </div>
                            <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white text-xs">
                                &times;
                            </button>
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-16 text-slate-400 text-xs">
                            <span className="material-symbols-outlined animate-spin text-2xl mb-2 text-indigo-400">sync</span>
                            <p>Loading standard mapping presets...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSave} className="space-y-5">
                            {/* Preset Metadata Card */}
                            <div className="bg-slate-800/60 border border-slate-700/80 rounded-xl p-4 space-y-4">
                                <div className="flex items-center justify-between border-b border-slate-700/60 pb-3">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[18px] text-indigo-400">edit_note</span>
                                        <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider">
                                            Preset Configuration: {form.sourceSystem} {form.entityType} &rarr; QBO {form.targetQBEntity}
                                        </h4>
                                    </div>
                                    <span className="text-[11px] text-slate-400">
                                        {form.mappingId ? `Preset ID: #${form.mappingId}` : 'New Preset'}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                    <div>
                                        <label className="block text-slate-400 mb-1 font-semibold">Preset Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={form.mappingName}
                                            onChange={e => setForm({ ...form, mappingName: e.target.value })}
                                            placeholder="e.g. Standard Passport Customer -> QBO Customer"
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-slate-400 mb-1 font-semibold">Target QuickBooks Entity</label>
                                        <select
                                            value={form.targetQBEntity}
                                            onChange={e => setForm({ ...form, targetQBEntity: e.target.value })}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white font-mono"
                                        >
                                            {qbEntities.map(ent => (
                                                <option key={ent} value={ent}>{ent}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-slate-400 mb-1 font-semibold">Description</label>
                                        <input
                                            type="text"
                                            value={form.description}
                                            onChange={e => setForm({ ...form, description: e.target.value })}
                                            placeholder="e.g. Standard mapping for customer contact and billing info"
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Field Mappings Table */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[16px] text-blue-400">tune</span>
                                        Field Mappings ({form.mappings.length})
                                    </h4>
                                    <button
                                        type="button"
                                        onClick={handleAddFieldRow}
                                        className="px-3 py-1.5 bg-blue-600/80 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[14px]">add</span>
                                        + Add Field Mapping
                                    </button>
                                </div>

                                <div className="border border-slate-700/80 rounded-xl overflow-hidden bg-slate-800/60 shadow-inner">
                                    <table className="w-full text-left text-xs border-collapse">
                                        <thead>
                                            <tr className="bg-slate-850 text-slate-400 border-b border-slate-700">
                                                <th className="p-3 font-semibold">Source Field(s)</th>
                                                <th className="p-3 font-semibold w-8 text-center">&rarr;</th>
                                                <th className="p-3 font-semibold">QuickBooks Target Field</th>
                                                <th className="p-3 font-semibold w-16 text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700/60">
                                            {form.mappings.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4} className="p-6 text-center text-slate-400">
                                                        No fields defined in this preset yet. Click "+ Add Field Mapping" above.
                                                    </td>
                                                </tr>
                                            ) : (
                                                form.mappings.map((m, idx) => (
                                                    <tr key={idx} className="hover:bg-slate-750 transition-colors">
                                                        <td className="p-3">
                                                            <input
                                                                type="text"
                                                                value={m.source || ''}
                                                                onChange={e => handleFieldChange(idx, 'source', e.target.value)}
                                                                placeholder="e.g. name, cust_no, addr_1"
                                                                className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-white font-mono text-xs focus:border-indigo-500 focus:outline-none"
                                                            />
                                                        </td>
                                                        <td className="p-3 text-center text-slate-500 font-bold">
                                                            &rarr;
                                                        </td>
                                                        <td className="p-3">
                                                            <input
                                                                type="text"
                                                                value={m.target || ''}
                                                                onChange={e => handleFieldChange(idx, 'target', e.target.value)}
                                                                placeholder="e.g. DisplayName, BillAddr.Line1"
                                                                className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-white font-mono text-xs focus:border-indigo-500 focus:outline-none"
                                                            />
                                                        </td>
                                                        <td className="p-3 text-right">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveFieldRow(idx)}
                                                                className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                                                                title="Delete field row"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">delete</span>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Save Action Footer */}
                            <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                                <div className="text-xs text-slate-400">
                                    Stored in <code>Sync.StandardMappings</code>. Can be applied en masse from any Connection Group.
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors border border-slate-700"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-md"
                                    >
                                        {saving ? (
                                            <>
                                                <span className="material-symbols-outlined text-[15px] animate-spin">sync</span>
                                                Saving Preset...
                                            </>
                                        ) : (
                                            <>
                                                <span className="material-symbols-outlined text-[15px]">save</span>
                                                Save Standard Preset
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};
